// pages/AdditionalInfoPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { 
  Layout, Form, Input, Button, Card, Typography, message, Select, 
  Space, Radio, Modal, Spin, Divider
} from 'antd';
import { 
  ArrowLeftOutlined,
  SaveOutlined,
  SearchOutlined,
  RobotOutlined,
  TagOutlined,
  ZoomInOutlined
} from '@ant-design/icons';
import { data, useNavigate } from 'react-router-dom';
import { ScanResult, BusinessCard } from '../types/businessCard';
import { getRoutePath, ROUTES } from '../config';
import { businessCardAPI } from '../services/api';
import { additionalAPI } from '../services/additional-api'; // 新しいAPI呼び出し
import { AssistInputState, SelectEnqueteInputAidModal } from '../services/assist-input';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { optionFieldAPI, OptionFieldListResponse } from '../services/option-field-api';
import OptionFieldGroup from '../components/OptionFields/OptionFieldGroup';
import { OptionFieldValidator } from '../utils/optionFieldValidation';
import { logApiError } from '../utils/errorLogger';
import { openUrl } from '../utils/linkify';
import { isInFlutter as isInFlutterUtil } from '../utils/flutterDetection';
import KeywordSelectModal from '../components/KeywordSelectModal';
import AssistInputModal from '../components/AssistInputModal';
import BusinessCardImageModal from '../components/BusinessCardImageModal';
import { FORCE_API_ERROR } from '../utils/debugFlags';

// Global declaration for the assist input modal for use in the window object
declare global {
  interface Window {
    SelectEnqueteInputAidModal?: typeof SelectEnqueteInputAidModal;
    State?: typeof AssistInputState;
    enqQuestions?: any[];
    enqCss?: string | any[];
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
    FlutterNavigation?: {
      postMessage: (message: string) => void;
    };
    FlutterDebug?: {
      postMessage: (message: string) => void;
    };
    FlutterError?: {
      postMessage: (message: string) => void;
    };
    flutterFooterState?: {
      isKeyboardVisible: boolean;
      footerHeight: number;
    };
  }
}

const { Header, Content } = Layout;
const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 追加情報入力用の型
interface AdditionalFormData {
  rankno?: number;
  notes?: string;
  keyword?: string; // キーワード選択用に追加
}

// 既存情報の型
interface ExistingAdditionalInfo {
  id: string;
  data: AdditionalFormData;
  lastUpdated: string;
}


// ランクオプションの型
interface RankOption {
  value: string;
  label: string;
}

// アシストテキストの型
interface AssistTextOption {
  enqNo: number;
  enqName: string;
}


const AdditionalInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<AdditionalFormData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [skipping, setSkipping] = useState<boolean>(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [ocrData, setOcrData] = useState<Record<string, any>>({});

  // Flutter環境の検出
  const isInFlutter = isInFlutterUtil();

  // Flutterフッターメニューの状態（Flutter環境でのみ使用）
  const [flutterFooterHeight, setFlutterFooterHeight] = useState<number>(0);

  const [frontPreviewOpen, setFrontPreviewOpen] = useState<boolean>(false);

  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [assistSearch, setAssistSearch] = useState<string>('');
  const [keywordModalVisible, setKeywordModalVisible] = useState<boolean>(false);
  const [assistModalVisible, setAssistModalVisible] = useState<boolean>(false);
  
  // API取得データの状態
  const [rankOptions, setRankOptions] = useState<RankOption[]>([]);
  const [keywordOptions, setKeywordOptions] = useState<any[]>([]);
  const [keywordLoadError, setKeywordLoadError] = useState<boolean>(false);
  const [assistOptions, setAssistOptions] = useState<AssistTextOption[]>([]);
  const [assistOptionsError, setAssistOptionsError] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // アシストテキストの状態
  const [selectedAssistEnq, setSelectedAssistEnq] = useState<number | null>(null);
  const [assistContentHtml, setAssistContentHtml] = useState<string>('');
  const [loadingAssistContent, setLoadingAssistContent] = useState<boolean>(false);
  const [defaultCustomerAssistNo, setDefaultCustomerAssistNo] = useState<number | null>(null);
  
  // 拡張フィールドの状態
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [loadingOptionFields, setLoadingOptionFields] = useState<boolean>(false);
  const [optionFieldValues, setOptionFieldValues] = useState<Record<string, any>>({});
  
  // テキストエリアの参照を保持
  const textAreaRef = useRef<any>(null);
  const assistContentRef = useRef<HTMLDivElement>(null);

  // アシスト入力モーダル内エラー表示用
  const [assistModalError, setAssistModalError] = useState<string>('');
  const assistModalErrorRef = useRef<HTMLDivElement>(null);

  // データフィールドの定義
  const dataFields = [
    { key: 'rankno', label: 'ランク', type: 'select' },
    { key: 'notes', label: '備考', type: 'textarea' }
  ];

  // Flutterフッター状態の監視（Flutter環境でのみ）
  useEffect(() => {
    if (!isInFlutter) return;

    const handleFlutterFooterStateChange = (event: any) => {
      const { footerHeight } = event.detail;
      setFlutterFooterHeight(footerHeight || 0);
    };

    // イベントリスナーを登録
    window.addEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);

    // 初期値の取得（Flutterから既に通知されている可能性がある）
    const initialState = (window as any).flutterFooterState;
    if (initialState && initialState.footerHeight !== undefined) {
      setFlutterFooterHeight(initialState.footerHeight);
    }

    return () => {
      window.removeEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);
    };
  }, [isInFlutter]);

  // アシスト入力およびキーワードモーダル表示時に背景スクロールを無効化
  useEffect(() => {
    if (assistModalVisible || keywordModalVisible) {
      // モーダル表示時：背景スクロールを無効化
      document.body.style.overflow = 'hidden';
      
      // アシスト入力モーダル用の特別なクラスを追加
      if (assistModalVisible) {
        document.body.classList.add('assist-modal-open');
      } else {
        document.body.classList.remove('assist-modal-open');
      }
    } else {
      // モーダル非表示時：背景スクロールを復元
      document.body.style.overflow = 'unset';
      document.body.classList.remove('assist-modal-open');
    }

    // クリーンアップ関数
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('assist-modal-open');
    };
  }, [assistModalVisible, keywordModalVisible]);

  // 既存のoptionfield値を適用する関数
  const applyExistingOptionFieldValues = async (optionFieldResult: any) => {
    try {
      const scanResult = await indexedDBStorage.getItem('scanResult');
      if (scanResult) {
        const result = JSON.parse(scanResult);
        const cardData = result.apiResponse?.customerdata || result.apiResponse?.duplicatedata;
        
        if (cardData && (cardData as any).optionfield) {
          const optionField = JSON.parse((cardData as any).optionfield);
          
          if (optionFieldResult.customeroptionfieldlist && Object.keys(optionField).length > 0) {
            const existingValues: Record<string, any> = {};
            
            optionFieldResult.customeroptionfieldlist.forEach((field: any) => {
              if (optionField[field.optionfieldkey] !== undefined) {
                existingValues[field.optionfieldkey] = optionField[field.optionfieldkey];
              }
            });
            
            if (Object.keys(existingValues).length > 0) {
              console.log('AdditionalInfoPage - 既存の拡張フィールド値を設定（拡張フィールド読み込み後）:', existingValues);
              setOptionFieldValues(existingValues);
            }
          }
        }
      }
    } catch (error) {
      console.error('AdditionalInfoPage - 既存拡張フィールド値適用エラー:', error);
    }
  };

  // ローカル顧客データを更新する関数
  const updateLocalCustomerData = async (formValues: any, updatedOptionField: any) => {
    try {
      const scanResult = await indexedDBStorage.getItem('scanResult');
      if (scanResult) {
        const result = JSON.parse(scanResult);
        
        // customerdata または duplicatedata を更新
        const isCustomerData = result.apiResponse?.customerdata;
        const targetPath = isCustomerData ? 'customerdata' : 'duplicatedata';

        console.log('AdditionalInfoPage - 保存先パス:', targetPath);
        console.log('AdditionalInfoPage - apiResponse構造:', {
          hasCustomerdata: !!result.apiResponse?.customerdata,
          hasDuplicatedata: !!result.apiResponse?.duplicatedata,
          hasDuplicatelist: !!result.apiResponse?.duplicatelist
        });

        if (result.apiResponse?.[targetPath]) {
          // フォーム値を反映
          if (formValues.rankno !== undefined) {
            result.apiResponse[targetPath].rankno = formValues.rankno;
          }
          if (formValues.notes !== undefined) {
            result.apiResponse[targetPath].remarks = formValues.notes;
          }
          
          // 拡張フィールド（optionfield）を更新
          result.apiResponse[targetPath].optionfield = JSON.stringify(updatedOptionField);
          
          // 更新されたscanResultを保存（scanResultとeditResultBackupの両方）
          await indexedDBStorage.setItem('scanResult', JSON.stringify(result));
          await indexedDBStorage.setItem('editResultBackup', JSON.stringify(result));
          console.log('AdditionalInfoPage - ローカル顧客データを更新しました:', {
            path: targetPath,
            rankno: formValues.rankno,
            remarks: formValues.notes,
            optionfield: updatedOptionField
          });
        }
      }
    } catch (error) {
      console.error('AdditionalInfoPage - ローカル顧客データ更新エラー:', error);
    }
  };

  
  // API初期化
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      setApiError(null);
      setKeywordLoadError(false);
      setAssistOptionsError(false);

      try {
        // 4本は互いの結果を使わないため並列で取得する（直列だと RTT × 4 かかる）。
        // getOptionFieldList はキャッシュミス時に throw する契約なので、1本の失敗で
        // 他3本の結果まで捨てないよう Promise.all ではなく allSettled を使う。
        setLoadingOptionFields(true);
        const [rankSettled, keywordSettled, assistSettled, optionFieldSettled] = await Promise.allSettled([
          additionalAPI.getRankList() as Promise<any>,
          (FORCE_API_ERROR
            ? Promise.resolve({ success: false, message: 'DEBUG: forced error', data: null })
            : additionalAPI.getKeywordList('01')) as Promise<any>,
          (FORCE_API_ERROR
            ? Promise.resolve({ success: false, message: 'DEBUG: forced error', data: null })
            : additionalAPI.getAssistTextList()) as Promise<any>,
          optionFieldAPI.getOptionFieldList() as Promise<any>,
        ]);

        // ランク一覧
        const rankResult = rankSettled.status === 'fulfilled'
          ? rankSettled.value
          : { success: false, message: String(rankSettled.reason) };
        if (rankResult.success && rankResult.data) {
          setRankOptions(rankResult.data.map((rank: any) => ({
            value: rank.id,
            label: rank.name
          })));
        } else {
          console.error('ランク一覧取得エラー:', rankResult.message);
          logApiError('AdditionalInfoPage.fetchInitialData', rankResult, '/api/getRankList', 'GET');
          // フォールバック値を設定
          setRankOptions([
            { value: 'S', label: 'S（最重要）' },
            { value: 'A', label: 'A（重要）' },
            { value: 'B', label: 'B（通常）' },
            { value: 'C', label: 'C（低）' },
            { value: 'D', label: 'D（最低）' }
          ]);
        }
        
        // キーワード一覧 (configFunctionDiv=01 備考欄用)
        const keywordResult = keywordSettled.status === 'fulfilled'
          ? keywordSettled.value
          : { success: false, message: String(keywordSettled.reason) };
        if (keywordResult.success && keywordResult.data) {
          try {
            // keywords が JSON 文字列の場合はパースする
            let keywordData = keywordResult.data;
            if (typeof keywordData === 'string') {
              keywordData = JSON.parse(keywordData);
            }
            
            // keywordlistプロパティがある場合（新しいAPI形式）
            if (keywordData.keywordlist && Array.isArray(keywordData.keywordlist)) {
              // キーワードオブジェクトをそのまま設定（keywordとkeywordcontentプロパティを保持）
              setKeywordOptions(keywordData.keywordlist);
            }
            // 旧APIの形式（result配列がある場合）
            else if (keywordData.result && Array.isArray(keywordData.result)) {
              // 旧形式の場合は新形式に変換
              const keywordList = keywordData.result.map((item: any) => ({
                keyword: item.keywordName || '',
                keywordcontent: item.keywordContent || item.keywordName || ''
              }));
              setKeywordOptions(keywordList);
            } else {
              message.error('キーワードの取得に失敗しました。');
              setKeywordOptions([]);
              setKeywordLoadError(true);
            }
          } catch (parseError) {
            console.error('キーワードデータのパースエラー:', parseError);
            message.error('キーワードの取得に失敗しました。');
            setKeywordOptions([]);
            setKeywordLoadError(true);
          }
        } else {
          console.error('キーワード一覧取得エラー:', keywordResult.message);
          logApiError('AdditionalInfoPage.fetchInitialData', keywordResult, '/api/getKeywordList', 'GET');
          message.error('キーワードの取得に失敗しました。');
          setKeywordOptions([]);
          setKeywordLoadError(true);
        }
        
        // アシスト入力一覧
        const assistResult = assistSettled.status === 'fulfilled'
          ? assistSettled.value
          : { success: false, message: String(assistSettled.reason) };
        if (assistResult.success && assistResult.data) {
          // 取得したデータが配列であることを確認
          if (Array.isArray(assistResult.data)) {
            // enqNo と enqName プロパティを持つオブジェクトの配列に変換
            if (typeof assistResult.data[0] === 'string') {
              // 文字列の配列の場合はオブジェクトに変換
              setAssistOptions(assistResult.data.map((text: string, index: number) => ({
                enqNo: index + 1,
                enqName: text
              })));
            } else {
              // すでにオブジェクトの配列の場合はそのまま使用
              setAssistOptions(assistResult.data);
            }

            // デフォルトアシストNoをuserから取得し、一覧に存在するものだけセット
            try {
              const userStr = await indexedDBStorage.getItem('user');
              if (userStr) {
                const user = JSON.parse(userStr);
                const defaultNo: number | null = user?.defaultcustomerassistno ?? null;

                const isValid =
                  defaultNo !== null &&
                  assistResult.data.some((opt: any) =>
                  typeof opt === 'string' ? false : opt.enqNo === defaultNo
                  );

                setDefaultCustomerAssistNo(isValid ? defaultNo : null);
              }
            } catch (e) {
              console.error('デフォルトアシストNo取得エラー:', e);
            }
          } else {
            message.error('アシストの取得に失敗しました。');
            setAssistOptions([]);
            setAssistOptionsError(true);
          }
        } else {
          console.error('アシスト入力一覧取得エラー:', assistResult.message);
          logApiError('AdditionalInfoPage.fetchInitialData', assistResult, '/api/getAssistTextList', 'GET');
          message.error('アシストの取得に失敗しました。');
          setAssistOptions([]);
          setAssistOptionsError(true);
        }
        
        // 拡張フィールド一覧
        try {
          if (optionFieldSettled.status === 'rejected') {
            throw optionFieldSettled.reason;
          }
          const optionFieldResult = optionFieldSettled.value;
          console.log('拡張フィールド一覧取得結果:', optionFieldResult);
          
          if (optionFieldResult.statuscode === 200) {
            setOptionFields(optionFieldResult);
            console.log('AdditionalInfoPage - 顧客拡張フィールド:', optionFieldResult.customeroptionfieldlist);
            
            // 拡張フィールド読み込み完了後、既存のoptionfield値があれば設定
            await applyExistingOptionFieldValues(optionFieldResult);
          } else {
            console.error('拡張フィールド取得エラー:', optionFieldResult.message);
          }
        } catch (optionFieldError) {
          console.error('拡張フィールド取得API呼び出しエラー:', optionFieldError);
          logApiError('AdditionalInfoPage.fetchInitialData', optionFieldError, '/api/getOptionFieldList', 'GET');
        } finally {
          setLoadingOptionFields(false);
        }
      } catch (error) {
        console.error('API初期化エラー:', error);
        logApiError('AdditionalInfoPage.fetchInitialData', error, '/api/multiple', 'GET');
        setApiError('データの読み込みに失敗しました。ネットワーク接続を確認してください。');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchInitialData();
  }, []);
  
  useEffect(() => {
    // IndexedDBから撮影結果を取得
    const fetchScanResult = async () => {
      try {
        const storedResult = await indexedDBStorage.getItem('scanResult');
        
        if (storedResult) {
          const result: ScanResult = JSON.parse(storedResult);
          setScanResult(result);

          // 既存情報があれば取得して表示
          const cardData = result.apiResponse.customerdata || result.apiResponse.duplicatedata;
          
          // 顧客データのoptionfieldをチェック
          if (cardData && (cardData as any).optionfield) {
            console.log('AdditionalInfoPage - 顧客データのoptionfield受信:', (cardData as any).optionfield);
            try {
              const optionField = JSON.parse((cardData as any).optionfield);
              console.log('AdditionalInfoPage - パース済み顧客optionfield:', optionField);
              
              // 拡張フィールドが読み込まれている場合、既存の値を設定
              if (optionFields?.customeroptionfieldlist && Object.keys(optionField).length > 0) {
                const existingValues: Record<string, any> = {};
                
                optionFields.customeroptionfieldlist.forEach(field => {
                  if (optionField[field.optionfieldkey] !== undefined) {
                    existingValues[field.optionfieldkey] = optionField[field.optionfieldkey];
                  }
                });
                
                if (Object.keys(existingValues).length > 0) {
                  console.log('AdditionalInfoPage - 既存の拡張フィールド値を設定:', existingValues);
                  setOptionFieldValues(existingValues);
                }
              }
            } catch (parseError) {
              console.log('AdditionalInfoPage - 顧客optionfield パースエラー:', parseError);
            }
          } else {
            console.log('AdditionalInfoPage - 顧客データにoptionfieldなし');
          }
          
          if (cardData) {
            checkForExistingInfo(cardData);
          } else {
            // 既存情報がない場合は初期値を設定
            form.setFieldsValue({
              rankno: undefined,
              notes: ''
            });
          }
        } else {
          message.error('撮影データが見つかりません');
          navigate(getRoutePath('/camera'));
        }
      } catch (error) {
        console.error('Failed to parse scan result:', error);
        message.error('データの読み込みに失敗しました');
        navigate(getRoutePath('/camera'));
      }
    };
    
    fetchScanResult();
  }, [form, navigate]);

  // 選択されたアシストオプションが変更されたときにAPIを呼び出す
  useEffect(() => {
    const fetchAssistContent = async () => {
      if (selectedAssistEnq !== null) {
        setLoadingAssistContent(true);
        try {
          // 選択したアシストEnqNoを更新（ID要素に設定）
          const hiddenInput = document.getElementById('selectEnqueteInputAid_customerRemarks') as HTMLInputElement;
          if (hiddenInput) {
            hiddenInput.value = selectedAssistEnq.toString();
          }
          
          const result = await additionalAPI.getAssistText(selectedAssistEnq);

          if (result.success && result.data) {
            try {
              // additionalAPI.getAssistText は内部で res.json() 済みのオブジェクトを返すが、
              // サーバー側実装次第で文字列が返ってくる可能性もあるため両対応する
              const parsedObj = typeof result.data === 'string'
                ? JSON.parse(result.data)
                : result.data;

              // エラー確認
              if (parsedObj.statuscode !== 200) {
                console.error('APIエラー:', parsedObj);
                setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
                return;
              }
              
              // 重要：取得したJSONオブジェクトをSelectEnqueteInputAidModalに直接設定
              SelectEnqueteInputAidModal.enquete = parsedObj;

              // 選択されたアシストオプションのタイトルを設定
              const selectedOption = assistOptions.find(opt => opt.enqNo === selectedAssistEnq);
              if (selectedOption) {
                SelectEnqueteInputAidModal.selectedEnqName = selectedOption.enqName;
              }

              // 質問リストの確認とフォーマット変換
              let questionList = [];
              if (parsedObj.questionlist) {
                if (Array.isArray(parsedObj.questionlist)) {
                  questionList = parsedObj.questionlist;
                } else {
                  console.warn('questionlistが配列ではありません:', parsedObj.questionlist);
                  try {
                    // 文字列の場合はJSON解析を試みる
                    if (typeof parsedObj.questionlist === 'string') {
                      questionList = JSON.parse(parsedObj.questionlist);
                    }
                  } catch (e) {
                    console.error('questionlistの解析に失敗:', e);
                  }
                }
              }
              
              // CSS設定の確認
              let css = parsedObj.css || '';
              
              // APIからのCSSと質問HTMLを結合し、スタイルを追加
              const overrideCss = `<style>
  .aptoenq_qa { margin: 0 !important; }
  .whitespace { white-space: pre-wrap; overflow-wrap: break-word; }
  .aptoenq_addtext { max-width: 100%; width: 100%; box-sizing: border-box; }
  textarea { font-family: inherit; font-size: inherit; padding: 5px; border: 1px solid #d9d9d9; border-radius: 4px; width: 100%; max-width: 100%; box-sizing: border-box; }
  label { display: block; margin-bottom: 10px; width: 100%; }
  .aptoenq_a { width: 100%; overflow-x: hidden; }
  .assist-content { width: 100%; overflow-x: hidden; }
  .assist-content * { max-width: 100%; }
</style>`;
              
              // HTML部分は作成せず、純粋な状態のみ保存する
              // 質問はJavaScriptで動的に生成する
              
              // 簡単なプレースホルダーのHTMLだけ設定
              const placeholderHtml = `<style>${css}</style>${overrideCss}`;
              setAssistContentHtml(placeholderHtml);
              
              // グローバル変数としてquestionsとcssを保存しておく
              window.enqQuestions = questionList;
              window.enqCss = css;
              
                          
              // aptoenqform要素を強制的にクリア
              const aptoenqform = document.getElementById('aptoenqform');
              if (aptoenqform) {
                aptoenqform.innerHTML = '';
              }
              
              // 少し待ってから初期化関数を呼び出す（レンダリング完了を待つ）
              setTimeout(() => {
                initializeAssistForm();
              }, 300);
              
            } catch (e) {
              console.error('JSONパースエラー:', e);
              setAssistContentHtml('<p>データの解析に失敗しました。</p>');
            }
          } else {
            console.error('アシストテキスト取得エラー:', result.message);
            logApiError('AdditionalInfoPage.fetchAssistContent', result, '/api/getAssistText', 'GET');
            setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
          }
        } catch (error) {
          console.error('アシストテキスト取得APIエラー:', error);
          logApiError('AdditionalInfoPage.fetchAssistContent', error, '/api/getAssistText', 'GET');
          setAssistContentHtml('<p>アシストテキストの取得に失敗しました。</p>');
        } finally {
          setLoadingAssistContent(false);
        }
      } else {
        setAssistContentHtml('');
      }
    };

    fetchAssistContent();
  }, [selectedAssistEnq]);

  // モーダルが表示されたら、aptoenqformを必ずクリアする
  useEffect(() => {
    if (assistModalVisible) {
      // aptoenqform要素を強制的にクリア
      const aptoenqform = document.getElementById('aptoenqform');
      if (aptoenqform) {
        aptoenqform.innerHTML = '';
      }
      
      // グローバルオブジェクトの設定
      window.SelectEnqueteInputAidModal = SelectEnqueteInputAidModal;
      window.State = AssistInputState;
      
      // エラーモーダル表示関数を設定
      (window as any).showAssistErrorModal = (title: string, content: string) => {
        console.log('AdditionalInfoPage: showAssistErrorModal呼び出し', title, content);
        Modal.error({
          title: title,
          content: content,
          okText: 'OK',
          centered: true,
        });
      };
      console.log('AdditionalInfoPage: showAssistErrorModal関数を設定しました');

      // アシスト入力モーダル内でエラーを表示する関数を設定
      (window as any).showAssistErrorInModal = (errorText: string) => {
        console.log('AdditionalInfoPage: showAssistErrorInModal呼び出し', errorText);
        setAssistModalError(errorText);

        // エラー表示位置にスクロール
        setTimeout(() => {
          if (assistModalErrorRef.current) {
            assistModalErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      };
      console.log('AdditionalInfoPage: showAssistErrorInModal関数を設定しました');

      // エラー状態をクリア
      setAssistModalError('');

      // コールバック関数を設定
      SelectEnqueteInputAidModal.callback = (text: string) => {
        insertAssistText(text);
      };
      
      // アンケート種類を設定
      SelectEnqueteInputAidModal.kind = 'customerRemarks';
      
      // ドロップダウンをリセット（選択解除）
      const dropdown = document.getElementById('selectEnqueteInputAid_menu') as HTMLSelectElement;
      if (dropdown) {
        dropdown.value = '';
      }
      
      // resetボタンとselectボタンを無効化
      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) {
        resetBtn.setAttribute('disabled', 'disabled');
      }
      
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) {
        selectBtn.setAttribute('disabled', 'disabled');
      }
    }
  }, [assistModalVisible]);

  
  // 既存の追加情報を検索する関数
  const checkForExistingInfo = (cardData: Partial<BusinessCard> | undefined) => {
    if (!cardData) return;
    
    const existingData = {
      rankno: cardData.rankno || undefined,
      notes: cardData.remarks || ''
    };
    
    // フォームに値を設定
    form.setFieldsValue(existingData);
  };
  
  
  // キーワードを挿入する関数（textToInsert はコンポーネント側で解決済み）
  const insertKeyword = (textToInsert: string) => {
    const currentValue = form.getFieldValue('notes') || '';
    const textarea = textAreaRef.current?.resizableTextArea?.textArea;
    const start = textarea?.selectionStart ?? 0;
    const end = textarea?.selectionEnd ?? 0;

    const newValue = currentValue.substring(0, start) + textToInsert + currentValue.substring(end);
    form.setFieldsValue({ notes: newValue });

    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(start + textToInsert.length, start + textToInsert.length);
      }
    }, 0);
  };
  
  // アシストテキストを挿入する関数
  const insertAssistText = (text: string) => {
    // テキストエリアの参照がある場合
    if (textAreaRef.current) {
      const textarea = textAreaRef.current?.resizableTextArea?.textArea;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      // 現在の値を取得
      const currentValue = form.getFieldValue('notes') || '';
      
      // テキストを現在のカーソル位置に挿入
      const newValue = currentValue.substring(0, start) + text + currentValue.substring(end);
      
      // フォームに新しい値をセット
      form.setFieldsValue({ notes: newValue });
      
      // モーダルを閉じる
      setAssistModalVisible(false);
      
      // フォーカスを戻す（遅延させてUIの更新後に実行）
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          // カーソル位置を挿入したテキストの後に設定
          const newPosition = start + text.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }
      }, 10);
    } else {
      // テキストエリアの参照がない場合は末尾に追加
      const currentValue = form.getFieldValue('notes') || '';
      const newValue = currentValue + (currentValue ? ' ' : '') + text;
      form.setFieldsValue({ notes: newValue });
      setAssistModalVisible(false);
    }
  };
  
  // フィルタリングされたキーワードオプションを取得
  const getFilteredKeywordOptions = () => {
    if (!keywordSearch) return keywordOptions;
    return keywordOptions.filter(keyword => {
      // オブジェクト形式の場合
      if (typeof keyword === 'object' && keyword.keyword) {
        return keyword.keyword.toLowerCase().includes(keywordSearch.toLowerCase());
      }
      // 文字列の場合（互換性のため）
      if (typeof keyword === 'string') {
        return keyword.toLowerCase().includes(keywordSearch.toLowerCase());
      }
      return false;
    });
  };
  
  // フィルタリングされたアシストオプションを取得
  const getFilteredAssistOptions = () => {
    if (!assistSearch) return assistOptions;
    return assistOptions.filter(option => 
      option.enqName.toLowerCase().includes(assistSearch.toLowerCase())
    );
  };
  
  // キーワード検索入力の変更ハンドラ
  const handleKeywordSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywordSearch(e.target.value);
  };
  
  // アシスト検索入力の変更ハンドラ
  const handleAssistSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAssistSearch(e.target.value);
  };
  
  // アシストオプション選択ハンドラ
  const handleAssistOptionChange = (value: number) => {
    setSelectedAssistEnq(value);

    // エラー表示をクリア
    setAssistModalError('');

    // フェッチと初期化は useEffect内のAPIコール完了後に行われるため、
    // ここでは何もしない - 選択状態を変更するだけ
  };
  
  // 質問データが設定された後に実行する初期化関数
  const initializeAssistForm = async () => {
    // 質問リストの存在チェック
    if (!window.enqQuestions) {
      return;
    }

    try {
      
      // まず要素の存在を確認
      let aptoenqform = document.getElementById('aptoenqform');
      
      // モーダル内の表示エリアを探す
      if (!aptoenqform) {
        const container = document.querySelector('.selectEnqueteInputAid .ant-modal-body [style*="border"]');
        if (container) {
          // aptoenqformが存在しない場合は新しく作成
          aptoenqform = document.createElement('div');
          aptoenqform.id = 'aptoenqform';
          aptoenqform.className = 'fontM';
          
          // 既存の要素を確認
          const existingAptoenq = container.querySelector('#aptoenqform');
          if (existingAptoenq) {
            aptoenqform = existingAptoenq as HTMLElement;
          } else {
            // 新しい要素を配置
            container.innerHTML = ''; // 既存のコンテンツをクリア
            container.appendChild(aptoenqform);
          }
        } else {
          console.error('モーダル内のコンテナが見つかりません');
          message.error('フォーム表示エリアが見つかりません。再度試してください。');
          return;
        }
      }
      
      
      // 要素の内容をクリア
      if (aptoenqform) {
        aptoenqform.innerHTML = '';
      }
      
      // 質問リストの確認と処理
      let processedQuestions = [];
      
      if (Array.isArray(window.enqQuestions)) {
        processedQuestions = window.enqQuestions;
      } else if (typeof window.enqQuestions === 'string') {
        try {
          processedQuestions = JSON.parse(window.enqQuestions);
        } catch(e) {
          console.error('質問リスト文字列の解析に失敗:', e);
          processedQuestions = [];
        }
      } else if (window.enqQuestions && typeof window.enqQuestions === 'object') {
        // オブジェクトの場合は値を使用
        const objValues = Object.values(window.enqQuestions);
        if (objValues.length > 0) {
          processedQuestions = objValues;
        }
      }
      
      // 質問リストデータを表示（デバッグ用）
      if (processedQuestions.length > 0) {
      } else {
        console.warn('処理された質問リストが空です');
        return;
      }
      
      // CSSデータの処理
      let cssData = [];
      if (typeof window.enqCss === 'string' && window.enqCss.trim() !== '') {
        // 文字列のCSSをセレクタと値のペアに変換する試み
        try {
          // CSSを簡易的に解析（完全ではありませんが基本的なケースに対応）
          const cssRules = window.enqCss.match(/([^{]+)\s*{\s*([^}]+)\s*}/g) || [];
          cssData = cssRules.map(rule => {
            const [, selector, styleText] = rule.match(/([^{]+)\s*{\s*([^}]+)\s*}/) || [];
            return [selector.trim(), styleText.trim()];
          });
        } catch(e) {
          console.warn('CSSの解析に失敗:', e);
        }
      } else if (Array.isArray(window.enqCss)) {
        cssData = window.enqCss;
      }
      
      // 再度要素の存在を確認（念のため）
      aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォーム表示要素の準備に失敗しました');
        return;
      }
      
      // AssistInputStateの新しいインスタンスを作成
      const state = new AssistInputState(processedQuestions, cssData);
      
      // リセットを呼び出して初期表示
      state.reset();
      
      // ボタンを有効化
      const resetBtn = document.getElementById('selectEnqueteInputAid_btnReset');
      if (resetBtn) {
        resetBtn.removeAttribute('disabled');
      }
      
      const selectBtn = document.getElementById('selectEnqueteInputAid_btnSelect');
      if (selectBtn) {
        selectBtn.removeAttribute('disabled');
      }

      // アシスト入力内のリンクを標準ブラウザで開くようにイベントリスナーを追加
      setTimeout(() => {
        const aptoenqform = document.getElementById('aptoenqform');

        if (aptoenqform) {
          const handleLinkClick = (e: Event) => {
            const target = e.target as HTMLElement;

            if (target.tagName === 'A' && target.hasAttribute('href')) {
              const href = target.getAttribute('href');

              if (href && href !== '#' && (href.startsWith('http://') || href.startsWith('https://'))) {
                e.preventDefault();
                openUrl(href);
              }
            }
          };

          aptoenqform.removeEventListener('click', handleLinkClick);
          aptoenqform.addEventListener('click', handleLinkClick);
        }
      }, 100);
    } catch (error) {
      console.error('Error initializing after dropdown change:', error);
      message.error('初期化中にエラーが発生しました');
    }
  };

  // HTMLコンテンツから直接テキストを挿入する関数
  const insertHtmlContent = () => {
    // 一時的なDOM要素を作成してHTMLを設定
    const tempDiv = assistContentRef.current;
    if (!tempDiv) return;
    
    // <style> タグをすべて削除して、CSSの内容をテキスト抽出対象から除外する
    const styleElements = tempDiv.querySelectorAll('style');
    styleElements.forEach(el => el.remove());
    
    // ラジオボタンなどが選択されている場合はそのテキストを抽出、なければ全体のテキストを使用する
    let selectedText = '';
    const selectedInputs = tempDiv.querySelectorAll('input:checked');
    if (selectedInputs.length > 0) {
      Array.from(selectedInputs).forEach(input => {
        const inputEl = input as HTMLInputElement;
        const enqContainer = inputEl.closest('.aptoenq_qa');
        const parentTitle = enqContainer?.querySelector('.questionTitle')?.textContent?.trim();
        if (parentTitle) {
          selectedText += parentTitle + ': ';
        }
        const parentLabel = inputEl.closest('label');
        if (parentLabel) {
          // ラジオボタンに関連するテキストエリアの値を取得
          const relatedTextarea = parentLabel.querySelector('textarea');
          let textareaValue = '';
          if (relatedTextarea) {
            textareaValue = (relatedTextarea as HTMLTextAreaElement).value;
          }
          
          // ラジオボタンのラベルテキストを取得
          let labelText = '';
          const labelSpan = parentLabel.querySelector('.whitespace');
          if (labelSpan) {
            labelText = labelSpan.textContent?.trim() || '';
          } else {
            labelText = parentLabel.textContent?.trim() || '';
          }
          
          // ラベルテキストとテキストエリアの値を組み合わせる
          selectedText += labelText;
          if (textareaValue) {
            selectedText += ' ' + textareaValue;
          }
          selectedText += ' ';
        } else {
          selectedText += inputEl.value + ' ';
        }
      });
    } else {
      // ラジオボタンが選択されていない場合は、テキストエリアの値があれば取得
      const textareas = tempDiv.querySelectorAll('textarea');
      if (textareas.length > 0) {
        Array.from(textareas).forEach(textarea => {
          const textareaValue = (textarea as HTMLTextAreaElement).value;
          if (textareaValue) {
            selectedText += textareaValue + ' ';
          }
        });
      }
      
      // テキストエリアの値もない場合は全体のテキストを使用
      if (!selectedText) {
        selectedText = tempDiv.textContent || tempDiv.innerText || '';
      }
    }
    
    // 不要な余白を除去して整形
    const cleanedText = selectedText.replace(/\s+/g, ' ').trim();
    
    // 生成したテキストを挿入する
    insertAssistText(cleanedText);
  };
  
  
  // 「スキップ」ボタンの処理
  const handleSkip = async () => {
    try {
      setSkipping(true);
      
      // スキップ処理
      message.success('追加情報をスキップしました');
      
      // フォロー登録画面へ遷移
      navigate(getRoutePath(ROUTES.FOLLOW_REGISTRATION));
    } catch (error) {
      console.error('スキップエラー:', error);
      message.error('処理に失敗しました');
    } finally {
      setSkipping(false);
    }
  };

  // 拡張フィールドの値変更ハンドラー
  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    console.log(`AdditionalInfoPage - 拡張フィールド変更: ${fieldKey} = ${value}`);
    setOptionFieldValues(prev => {
      const newValues = {
        ...prev,
        [fieldKey]: value
      };
      console.log('AdditionalInfoPage - 更新後の拡張フィールド値:', newValues);
      return newValues;
    });
  };
  
  // 「登録」ボタンの処理
  const handleSubmit = async () => {
    try {
      // フォームのバリデーション
      const values = await form.validateFields();
      
      // 拡張フィールドのバリデーション
      if (optionFields?.customeroptionfieldlist) {
        const optionValidationResult = OptionFieldValidator.validateFields(
          optionFieldValues,
          optionFields.customeroptionfieldlist
        );
        
        if (!optionValidationResult.isValid) {
          optionValidationResult.errors.forEach(error => {
            message.error(error.message);
          });
          return;
        }
      }
      
      setLoading(true);
      
      // 現在の日時を ISO 8601 形式でフォーマット (YYYY-MM-DDThh:mm:ss.sss+0000)
      const now = new Date();
      
      // ISO文字列を取得し、必要に応じて時間帯を調整
      let isoString = now.toISOString(); // 例: "2025-03-20T12:34:56.789Z"
      
      // Zを+0000に置き換え
      const currentDateTime = isoString.replace('Z', '+0000');
      
      // ScanResultから顧客番号を取得
      const editingCustomerNo = await indexedDBStorage.getItem('editingCustomerNo');
      if (!editingCustomerNo) {
        message.error('顧客情報が見つかりません');
        setLoading(false);
        return;
      }
      
      // 1. 追加情報APIに送信するデータ
      /*const additionalData = {
        customerno: parseInt(customerNo),
        followdate: currentDateTime,
        followcontent: values.notes || ''
      };*/
      
      // 2. 顧客情報更新API用のデータを準備

      const scanResult = await indexedDBStorage.getItem('scanResult');
      if (!scanResult) {
        throw new Error('スキャン結果が見つかりません');
      }

      //顧客名とメールアドレスか電話番号を取得
      const scanData = JSON.parse(scanResult);
            
      // 顧客情報更新API用のデータを準備
      // nullは条件句生成でエラーになるため、undefined に置き換える
      const currentData = scanData.apiResponse.customerdata || scanData.apiResponse.duplicatedata;
      // 既存のoptionfieldと拡張フィールドの値をマージ
      let mergedOptionField = {};
      
      // 既存のoptionfieldを取得
      if (currentData?.optionfield) {
        try {
          mergedOptionField = JSON.parse(currentData.optionfield);
        } catch (error) {
          console.error('既存optionfield解析エラー:', error);
        }
      }
      
      // 拡張フィールドの値をマージ
      if (Object.keys(optionFieldValues).length > 0) {
        console.log('AdditionalInfoPage - 登録時の拡張フィールド値:', optionFieldValues);
        mergedOptionField = { ...mergedOptionField, ...optionFieldValues };
      }
      
      console.log('AdditionalInfoPage - 保存するマージ済みoptionfield:', mergedOptionField);

      const customerUpdateData = {
        ...currentData,
        rankno: values.rankno|| null,
        remarks: values.notes || null,
        // マージされたオプションフィールド
        optionfield: JSON.stringify(mergedOptionField)
      };
      
      
      // 3. 追加情報APIを呼び出し
      /*
      const additionalResult = await additionalAPI.saveAdditionalInfo(additionalData);
      
      if (!additionalResult.success) {
        throw new Error(additionalResult.message || '追加情報の保存に失敗しました');
      }
        */
      
      // 4. 顧客情報更新APIを呼び出し
      const token = await indexedDBStorage.getItem('token');
      if (!token) {
        throw new Error('認証情報がありません');
      }
      
      // businessCardAPI.editCustomer を使用して顧客情報を更新
      const customerUpdateResult = await businessCardAPI.editCustomer(customerUpdateData);
      
      // レスポンスの確認
      if (customerUpdateResult.statuscode !== 200) {
        throw new Error(customerUpdateResult.message || '顧客情報の更新に失敗しました');
      }
      
      // ローカルの顧客情報（scanResult）も更新
      await updateLocalCustomerData(values, mergedOptionField);
      
      // 両方のAPI呼び出しが成功した場合
      message.success('情報を登録しました');

      // scanResultはフォロー登録ページでも必要なので残す

      // フォロー登録画面に遷移
      navigate(getRoutePath(ROUTES.FOLLOW_REGISTRATION));
      
    } catch (error: any) {
      console.error('顧客情報登録エラー:', error);
      logApiError('AdditionalInfoPage.handleSubmit', error, '/api/editCustomer', 'POST');
      message.error(error.message || '登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };
  
  
  // 初期読み込み中の表示
  if (initialLoading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ 
          background: '#fff', 
          padding: '0 16px', 
          display: 'flex', 
          alignItems: 'center' 
        }}>
          <Button 
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(getRoutePath('/edit'))}
            style={{ 
              backgroundColor: 'white',
              borderColor: '#d9d9d9',
              borderWidth: '2px',
              color: '#595959',
              marginRight: '8px',
              width: '32px',
              height: '32px',
              minWidth: '32px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <div style={{ flex: 1, textAlign: 'center' }}>
            追加情報
          </div>
        </Header>
        
        <Content style={{
          padding: '16px',
          paddingBottom: `${16 + flutterFooterHeight}px`,
          background: '#f0f2f5',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>データを読み込み中...</div>
        </Content>
      </Layout>
    );
  }
  
  // APIエラーがある場合のエラー表示
  if (apiError) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ 
          background: '#fff', 
          padding: '0 16px', 
          display: 'flex', 
          alignItems: 'center' 
        }}>
          <Button 
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(getRoutePath('/edit'))}
            style={{ 
              backgroundColor: 'white',
              borderColor: '#d9d9d9',
              borderWidth: '2px',
              color: '#595959',
              marginRight: '8px',
              width: '32px',
              height: '32px',
              minWidth: '32px',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <div style={{ flex: 1, textAlign: 'center' }}>
            追加情報
          </div>
        </Header>

        <Content style={{
          padding: '16px',
          paddingBottom: `${16 + flutterFooterHeight}px`,
          background: '#f0f2f5'
        }}>
          <Card style={{ marginBottom: 16, backgroundColor: '#ffebee' }}>
            <div style={{ color: '#d32f2f' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>エラー</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{apiError}</div>
              <Button 
                type="primary" 
                style={{ marginTop: 16 }} 
                onClick={() => navigate(getRoutePath('/edit'))}
              >
                編集画面に戻る
              </Button>
            </div>
          </Card>
        </Content>
      </Layout>
    );
  }
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <Button 
          shape="circle"
          size="small"
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(getRoutePath('/edit'))}
          style={{ 
            backgroundColor: 'white',
            borderColor: '#d9d9d9',
            borderWidth: '2px',
            color: '#595959',
            marginRight: '8px',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
        <div style={{ flex: 1, textAlign: 'center' }}>
          追加情報
        </div>
      </Header>

      <Content style={{
        padding: '16px',
        paddingBottom: `${16 + flutterFooterHeight + 80}px`, // 16px基本余白 + Flutterフッター高さ + 固定ボタン高さ(約80px)
        background: '#f0f2f5',
        overflowY: 'auto'
      }}>
        {/* 読み取った名刺画像 */}        
        {(scanResult?.apiResponse?.customerdata?.businesscardfrontimage || scanResult?.apiResponse?.duplicatedata?.businesscardfrontimage) && (
          <Card style={{ marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <img
                  src={scanResult.apiResponse.customerdata?.businesscardfrontimage || scanResult.apiResponse.duplicatedata?.businesscardfrontimage}
                  alt="撮影した名刺"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
                <button
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    background: 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    cursor: 'pointer',
                    color: '#fff',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => setFrontPreviewOpen(true)}
                >
                  <ZoomInOutlined />
                </button>
              </div>
            </div>
          </Card>
        )}
        
        {/* 追加情報入力フォーム */}
        <Card style={{ marginBottom: 16 }}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{
              rankno: undefined,
              notes: ''
            }}
          >
            {/* ランク入力フィールド */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8 }}>
                <Text strong>ランク</Text>
              </div>
              
              <Form.Item name="rankno" style={{ marginBottom: 0 }}>
                <Select placeholder="ランクを選択">
                  {rankOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            
            
            {/* 備考入力フィールド */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ 
                marginBottom: 8, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Text strong>備考</Text>
                <Space>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<TagOutlined />}
                    onClick={() => setKeywordModalVisible(true)}
                  >
                    キーワード入力
                  </Button>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<RobotOutlined />}
                    onClick={() => {
                      setSelectedAssistEnq(defaultCustomerAssistNo);
                      setAssistModalVisible(true);
                      
                      // Add mobile keyboard handling for assist modal
                      if (window.innerWidth <= 768) {
                        // Save current scroll position
                        const originalScrollY = window.scrollY;
                        
                        const setupKeyboardHandling = () => {
                          // Find the assist input modal specifically (should be the topmost modal)
                          const modals = document.querySelectorAll('.ant-modal');
                          const modal = modals[modals.length - 1]; // Get the last (topmost) modal
                          if (!modal) return;
                          
                          const handleInputFocus = (input: Element) => {
                            const modalElement = modal as HTMLElement;
                            modalElement.style.position = 'fixed !important';
                            modalElement.style.top = '0px !important';
                            modalElement.style.left = '0 !important';
                            modalElement.style.right = '0 !important';
                            modalElement.style.margin = '0 auto !important';
                            modalElement.style.transform = 'none !important';
                            modalElement.style.zIndex = '9999 !important';
                            modalElement.style.width = 'auto !important';
                            modalElement.style.maxWidth = '90vw !important';
                            
                            // Find assist input content area and scroll input to top
                            setTimeout(() => {
                              const modalBody = modal.querySelector('.ant-modal-body') as HTMLElement;
                              const aptoenqform = modal.querySelector('#aptoenqform') as HTMLElement;
                              
                              if (modalBody && aptoenqform && input instanceof HTMLElement) {
                                // Force reflow to ensure correct positioning
                                void modalBody.offsetHeight;
                                
                                // Try multiple methods to ensure input is visible
                                // Method 1: scrollIntoView on the input element
                                try {
                                  input.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                    inline: 'nearest'
                                  });
                                } catch (e) {
                                  // Fallback: manual scroll calculation
                                  const inputTop = input.offsetTop || 0;
                                  const targetScrollTop = Math.max(0, inputTop - 50);
                                  
                                  modalBody.scrollTo({
                                    top: targetScrollTop,
                                    behavior: 'smooth'
                                  });
                                }
                              }
                            }, 300);
                          };
                          
                          const addFocusListeners = () => {
                            // Target only inputs within the assist content area
                            const inputs = modal.querySelectorAll('#aptoenqform input, #aptoenqform textarea, .ant-modal-body input, .ant-modal-body textarea');
                            inputs.forEach(input => {
                              input.removeEventListener('focus', () => handleInputFocus(input));
                              input.addEventListener('focus', () => handleInputFocus(input));
                            });
                          };
                          
                          addFocusListeners();
                          
                          const observer = new MutationObserver(() => {
                            setTimeout(addFocusListeners, 300);
                          });
                          
                          observer.observe(modal, {
                            childList: true,
                            subtree: true
                          });
                          
                          // Store original scroll position for restoration
                          (modal as any)._originalScrollY = originalScrollY;
                        };
                        
                        setTimeout(setupKeyboardHandling, 1500);
                        setTimeout(setupKeyboardHandling, 3000);
                        setTimeout(setupKeyboardHandling, 5000);
                      }
                    }}
                  >
                    アシスト入力
                  </Button>
                </Space>
              </div>
              
              <Form.Item name="notes" style={{ marginBottom: 0 }}>
                <TextArea 
                  placeholder="備考を入力"
                  autoSize={{ minRows: 3 }}
                  ref={textAreaRef}
                />
              </Form.Item>
            </div>
          </Form>
        </Card>

        {/* 拡張フィールド表示 */}
        {optionFields?.customeroptionfieldlist && optionFields.customeroptionfieldlist.length > 0 && (
          <div className="option-field-group">
            <OptionFieldGroup
              title=""
              fields={optionFields.customeroptionfieldlist.filter(field => field.optionfieldkey !== 'furigana')}
              values={optionFieldValues}
              onFieldChange={handleOptionFieldChange}
              prefix="customer"
            />
          </div>
        )}
      </Content>
      
      {/* フラットなフルワイドボタンエリア */}
      <div style={{
        display: 'flex',
        position: 'sticky',
        bottom: isInFlutter ? `${flutterFooterHeight}px` : 0,
        width: '100%',
        zIndex: 10
      }}>
        <Button 
          type="default" 
          onClick={handleSkip}
          loading={skipping}
          style={{ 
            flex: '1 1 50%', 
            height: '56px', 
            borderRadius: 0,
            borderTop: '1px solid #e8e8e8',
            borderRight: '1px solid #e8e8e8',
            borderBottom: 'none',
            borderLeft: 'none',
            boxShadow: 'none'
          }}
        >
          スキップ
        </Button>
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={handleSubmit}
          loading={loading}
          style={{ 
            flex: '1 1 50%', 
            height: '56px', 
            borderRadius: 0,
            borderTop: '1px solid #0693e3',
            borderBottom: 'none',
            boxShadow: 'none'
          }}
        >
          登録
        </Button>
      </div>
      
      {/* キーワード入力モーダル */}
      <KeywordSelectModal
        open={keywordModalVisible}
        options={keywordOptions}
        onClose={() => setKeywordModalVisible(false)}
        onSelect={insertKeyword}
        error={keywordLoadError}
        destroyOnClose
        maskClosable={false}
        modalRender={(modal) => <div style={{ pointerEvents: 'auto', cursor: 'default' }}>{modal}</div>}
        transitionName=""
        maskTransitionName=""
        flutterFooterHeight={flutterFooterHeight}
      />
      
      {/* アシスト入力モーダル */}
      <AssistInputModal
        open={assistModalVisible}
        onClose={() => {
          setAssistModalVisible(false);
          setSelectedAssistEnq(null);
        }}
        options={assistOptions}
        selectedValue={selectedAssistEnq}
        onValueChange={handleAssistOptionChange}
        contentHtml={assistContentHtml}
        loading={loadingAssistContent}
        error={assistModalError}
        errorRef={assistModalErrorRef}
        contentRef={assistContentRef}
        optionsError={assistOptionsError}
        hiddenInputId="selectEnqueteInputAid_customerRemarks"
        width={600}
        bodyStyle={{ overflow: 'hidden', padding: '2px' }}
      />

      {/* 名刺表面 拡大プレビュー */}
      <BusinessCardImageModal
        open={frontPreviewOpen}
        src={scanResult?.apiResponse?.customerdata?.businesscardfrontimage || scanResult?.apiResponse?.duplicatedata?.businesscardfrontimage}
        alt="撮影した名刺"
        flutterFooterHeight={flutterFooterHeight}
        onClose={() => setFrontPreviewOpen(false)}
      />
    </Layout>
  );
};

export default AdditionalInfoPage;